import React, { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../components/ui/use-toast';
import { 
  Settings, Bell, Globe, Shield, Palette, Clock, 
  Monitor, Smartphone, Mail, MessageSquare, Save,
  Eye, Database, Download, Upload, RefreshCw, AlertTriangle,
  X, CheckCircle, Sparkles, Volume2
} from 'lucide-react';
import { loadSoundSettings, saveSoundSettings, playNotificationSound } from '../utils/notificationSounds';

const SettingsPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  
  const _initialSound = loadSoundSettings();
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: true,
    newCases: true,
    statusUpdates: true,
    assignments: true,
    emergencies: true,
    systemAlerts: false,
    // Sesli bildirim tercihleri (localStorage'dan yüklenir)
    soundEnabled:       _initialSound.soundEnabled,
    soundNewCases:      _initialSound.soundNewCases,
    soundAssignments:   _initialSound.soundAssignments,
    soundEmergencies:   _initialSound.soundEmergencies,
    soundSystemAlerts:  _initialSound.soundSystemAlerts,
  });

  const [preferences, setPreferences] = useState({
    language: 'tr',
    theme: 'light',
    timezone: 'Europe/Istanbul',
    autoRefresh: true,
    refreshInterval: 30,
    itemsPerPage: 50,
    dateFormat: 'dd/mm/yyyy',
    timeFormat: '24h',
  });

  const [privacy, setPrivacy] = useState({
    showOnlineStatus: true,
    allowDataExport: true,
    shareUsageData: false,
    enableAnalytics: true,
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  // Changelog data - versioned
  const changelog = {
    version: 'v3.1.0',
    date: '10.05.2026',
    versions: [
      {
        version: 'v3.1.0',
        date: '10.05.2026',
        label: 'Güncel Sürüm',
        badgeClass: 'bg-purple-600',
        changes: [
          {
            type: 'feature',
            title: 'Kurumlar Vaka Mutabakatı (external_institution)',
            description:
              'Kurum detay düzenleme alanına "Vaka Mutabakatı" sekmesi eklendi. Hastane mutabakat sistemiyle aynı mantık: "Mutabıkız / Değiliz" onayı, hata gerekçelendirme ve toplu işaretleme. external_institution rolü sadece mutabakat sekmesini görür.',
          },
          {
            type: 'feature',
            title: 'Kota Bazlı Dinamik Fiyatlandırma',
            description:
              'Kurum ayarlarına "Sabit Anlaşma Tutarı" (contract_fixed_amount) alanı eklendi. Ücretsiz vaka kotası dolduğunda Bölgesel Fiyatlandırma tarifeleri otomatik devreye girer ve vakaya seçilen bölgeye göre hakediş yansır.',
          },
          {
            type: 'feature',
            title: 'Çatı Kurum Mimarisi (external_parent_institution)',
            description:
              'Kurumlar arasında üst/alt hiyerarşi (parent_institution_id) eklendi. Medicana ve Anadolu gibi çatı kurumlara yeni external_parent_institution rolü tanımlandı. Çatı kullanıcılar tüm alt kurumların vakalarını, mutabakat özetlerini ve hizmet raporlarını görüntüleyebilir.',
          },
          {
            type: 'improvement',
            title: 'Organizasyon Faaliyet Raporu (v3.2.0)',
            description:
              'Organizasyonlara doktor ücreti ve ek personel detayları eklendi. Organizasyon vakalarını listeleme, PDF ve Excel formatında faaliyet raporu indirme desteği. Yeni vaka oluştururken aktif organizasyon seçimi ve organizasyon ekiplerinden atama.',
          },
        ],
      },
      {
        version: 'v3.0.0',
        date: '18.04.2026',
        label: 'Önceki Sürüm',
        badgeClass: 'bg-blue-600',
        changes: [
          {
            type: 'feature',
            title: '112 Hastane Dış Kullanıcı Rolü (external_hospital)',
            description:
              'Yeni "external_hospital" rolü eklendi. Bu kullanıcılar sadece kendi hastanelerine ait vakaları, planlı vakaları ve hastane kartını görüntüleyebilir. Hizmet raporunda bağlı olduğu başkanlık ve hastane otomatik seçilir.',
          },
          {
            type: 'feature',
            title: 'Mutabakat Arayüzü ',
            description:
              'Hastane detay modalında "Mutabıkız / Mutabık Değiliz" mutabakat arayüzü eklendi. Toplu vaka seçimi (checkbox), vaka no\'ya tıklandığında özet popup, "Mutabık Değiliz" için zorunlu gerekçe açıklama alanı.',
          },
          {
            type: 'feature',
            title: 'Anlık Durum Senkronizasyonu ',
            description:
              'Onaylanan/reddedilen vakalar mutabakat durumu anlık olarak güncellenir. Ay sonunda external_hospital kullanıcılarına otomatik mutabakat hatırlatma bildirimi gönderilir. Adminler "Bildirim Gönder" butonu ile manuel hatırlatma yapabilir.',
          },
          {
            type: 'feature',
            title: 'E-posta Gönderme Sistemi ',
            description:
              'Hastane kota düzenleme alanına "Mail Gönder" butonu eklendi. Supabase Edge Functions + SMTP üzerinden iki şablon desteği: Mutabakat Hatırlatması ve Mutabakat Onay Bildirimi. Hastane formuna 3 adet yetkili kişi ad ve e-posta alanı eklendi.',
          },
          {
            type: 'feature',
            title: 'E-posta Şablonları ',
            description:
              'Hatırlatma ve mutabakat onay e-posta şablonları EMT imza bloğu (Mustafa Özgenç, logo, iletisim bilgileri) ile birlikte hazırlandı.',
          },
          {
            type: 'improvement',
            title: 'Veritabanı Güncellemeleri',
            description:
              'Users tablosuna hospital_id kolonu, cases tablosuna reconciliation_status/note/date/reconciled_by, hospitals tablosuna contact_email_1/2/3 ve contact_name_1/2/3 kolonları eklendi. Rol kısıtlaması güncellendi.',
          },
        ],
      },
      {
        version: 'v2.3.0',
        date: '10.04.2026',
        label: 'Önceki Sürüm',
        badgeClass: 'bg-green-600',
        changes: [
          {
            type: 'feature',
            title: 'Başkanlık Vaka Atama Modülü',
            description:
              '112 Başkanlığı kullanıcıları artık "Yeni Vaka" sayfasından vaka oluşturabilir. Vakayı Veren bölümünde yalnızca "Başkanlık" seçeneği görünür; başkanlık ve bağlı hastane seçildiğinde kota uyarısı gösterilir.',
          },
          {
            type: 'feature',
            title: 'EK-3 Belge Zorunluluğu',
            description:
              'Başkanlık kullanıcıları 1. aşama vaka oluşturduktan hemen sonra EK-3 belgesi yükleyebilir (5 MB sınırı). Belge yükleme toggle buton ile açılır/kapatılır. Yükleme, değiştirme, indirme ve silme işlemleri desteklenmektedir.',
          },
          {
            type: 'feature',
            title: 'Salt Okunur 2. Aşama (Başkanlık)',
            description:
              'Başkanlık yetkisindeki kullanıcılar 2. aşama verilerini görüntüleyebilir; ancak düzenleme yetkisi sunucu taraflı kilitlidir. 3. aşamada EK-3 belgesi görüntüleme ve yönetim butonları mevcuttur.',
          },
          {
            type: 'feature',
            title: 'Başkanlık Atama Bildirim Sistemi',
            description:
              'Başkanlık kullanıcısı vaka oluşturduğunda sistem otomatik olarak admin, süper admin, supervisor, operatör ve ekip lideri kullanıcılara "presidency_case_assignment" tipinde bildirim gönderir.',
          },
        ],
      },
      {
        version: 'v2.2.0',
        date: '14.03.2026',
        label: 'Önceki Sürüm',
        badgeClass: 'bg-blue-600',
        changes: [
          {
            type: 'feature',
            title: '112 Başkanlığı Dış Kullanıcı Rolü',
            description:
              'Yeni "external_presidency" rolü eklendi. Bu rol ile bir kullanıcı tüm 112 başkanlıklarına ait vakaları görüntüleyebilir, hizmet raporuna ve hastaneler sayfasına erişebilir. Kullanıcılar vakaları düzenleyemez, yeni vaka oluşturamaz. Dashboard ve vaka listesi otomatik olarak yılbaşından (01.01.YYYY) başlayarak filtrelenir.',
          },
          {
            type: 'improvement',
            title: 'Hizmet Raporunda Başkanlık Bazlı Filtreleme',
            description:
              '112 Başkanlığı kullanıcısı Hizmet Raporu\'nda "Başkanlık" dropdown\'ı ile sadece başkanlık kurumlarını görür. "Tüm Başkanlıklar" seçildiğinde her başkanlığın vaka sayısı kart olarak gösteriliyor.',
          },
          {
            type: 'improvement',
            title: 'Vaka Sayısı Tutarlılığı',
            description:
              '112 Başkanlığı kullanıcısının gördüğü vaka sayısı artık admin ile tutarlı. billing_institution bazlı filtreleme kullanılarak tüm vakalarda doğru sayım sağlanıyor.',
          },
          {
            type: 'feature',
            title: '112 Başkanlıkları Entegrasyonu',
            description:
              '"Vakayı Veren" bölümüne "112 Başkanlıkları" seçeneği eklendi. Seçim yapıldığında ilgili başkanlığa bağlı hastaneler listeleniyor ve seçilen hastane vakaya kaydediliyor.',
          },
          {
            type: 'feature',
            title: 'Hastane Kota Sistemi',
            description:
              'Hastane kartlarında aylık kota sayacı eklendi. Hastane kartına tıklandığında detay modalı açılıyor; aylık kota, vaka başı ücret ve kota notu düzenlenebiliyor.',
          },
          {
            type: 'feature',
            title: 'Mutabakat Formu (Excel)',
            description:
              'Hastane kartında tarih aralığı seçilerek "Mutabakat Oluştur" butonu ile 2 sayfalık Excel raporu indirilebiliyor.',
          },
          {
            type: 'feature',
            title: '112 Vakası Etiketi',
            description:
              'Vaka listelerinde case_giver_type = "presidency" olan vakalar turkuvaz renkli "112 Vakası" etiketi ile işaretleniyor.',
          },
        ],
      },
      {
        version: 'v2.0.0',
        date: '25.02.2026',
        label: '',
        badgeClass: 'bg-gray-400',
        changes: [
          {
            type: 'improvement',
            title: 'Excel Export Güncellendi',
            description:
              'Vaka Excel dışa aktarımında hasta adı-soyadı birleşik kolonu eklendi, "112 Başkanlığı" için yeni "Vakayı Veren Hastane (112)" sütunu ve Türkçe etiketler eklendi.',
          },
          {
            type: 'improvement',
            title: 'Ekip Listesi Sayısal Sıralama',
            description:
              'Ekip Durumu sayfası ve 2. Aşama atanan ekip dropdown\'unda ekipler M-1, M-2, ..., M-30, T-1, T-2 şeklinde sayısal sırayla listeleniyor.',
          },
          {
            type: 'improvement',
            title: 'Planlı Vaka Toast Bildirimi',
            description:
              'Planlı vaka ve organizasyon yaklaşıyor bildirimleri artık kapatma (✕) butonuna sahip. Bildirim 8 saniye görüntüleniyor.',
          },
          {
            type: 'feature',
            title: 'Arvento Araç Konumu Entegrasyonu',
            description:
              'Ekip Durumu sayfasında her ekip için Arvento araç takip sistemi ile son konum bilgisi harita üzerinde gösteriliyor.',
          },
          {
            type: 'feature',
            title: 'Organizasyonlarda Ekip Atamaları',
            description:
              'Organizasyon oluştururken veya düzenlerken ambulans sayısına göre otomatik ekip atama slotları oluşturuluyor.',
          },
          {
            type: 'feature',
            title: 'Vakada Organizasyon Seçeneği',
            description:
              'Yeni vaka oluştururken "Vakayı Veren" bölümüne "Organizasyon" seçeneği eklendi. Aktif organizasyonlar listeleniyor.',
          },
          {
            type: 'feature',
            title: 'Tıbbi Tanımlar Sayfası',
            description:
              'Teşhisler (ICD-10), İlaçlar ve Malzemeler için yönetim sayfası eklendi. Sadece admin ve süper admin düzenleyebilir.',
          },
          {
            type: 'feature',
            title: 'Vaka 3. Aşamada Teşhis, İlaç ve Malzeme',
            description:
              'Vaka tamamlama formuna teşhis, ilaç ve malzeme alanları eklendi. Tıbbi tanımlardan arama ile seçim yapılabiliyor.',
          },
          {
            type: 'improvement',
            title: 'Raporlar Sayfası Yenilendi',
            description:
              'Hizmet Raporu sayfası profesyonel görünüme kavuşturuldu. Pasta grafiği, yatay bar grafik ve alan grafik ile zenginleştirildi.',
          },
        ],
      },
    ],
  };

  const handleNotificationChange = (key, value) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handlePrivacyChange = (key, value) => {
    setPrivacy(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const saveSettings = () => {
    // Ses tercihlerini localStorage'a kaydet
    saveSoundSettings({
      soundEnabled:      notifications.soundEnabled,
      soundNewCases:     notifications.soundNewCases,
      soundAssignments:  notifications.soundAssignments,
      soundEmergencies:  notifications.soundEmergencies,
      soundSystemAlerts: notifications.soundSystemAlerts,
    });

    // Simulate API call
    setTimeout(() => {
      setHasChanges(false);
      toast({
        title: "Başarılı",
        description: "Ayarlar başarıyla kaydedildi",
        variant: "success"
      });
    }, 1000);
  };

  const resetSettings = () => {
    setNotifications({
      email: true,
      push: false,
      sms: true,
      newCases: true,
      statusUpdates: true,
      assignments: true,
      emergencies: true,
      systemAlerts: false,
      soundEnabled: true,
      soundNewCases: true,
      soundAssignments: true,
      soundEmergencies: true,
      soundSystemAlerts: true,
    });
    setPreferences({
      language: 'tr',
      theme: 'light',
      timezone: 'Europe/Istanbul',
      autoRefresh: true,
      refreshInterval: 30,
      itemsPerPage: 50,
      dateFormat: 'dd/mm/yyyy',
      timeFormat: '24h',
    });
    setPrivacy({
      showOnlineStatus: true,
      allowDataExport: true,
      shareUsageData: false,
      enableAnalytics: true,
    });
    setHasChanges(true);
    
    toast({
      title: "Sıfırlandı",
      description: "Ayarlar varsayılan değerlere sıfırlandı",
      variant: "default"
    });
  };

  const exportData = () => {
    toast({
      title: "Veri Dışa Aktarma",
      description: "Bu özellik yakında gelecek",
      variant: "default"
    });
  };

  const Switch = ({ checked, onCheckedChange, disabled = false }) => (
    <button
      type="button"
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        ${checked ? 'bg-blue-600' : 'bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      onClick={() => !disabled && onCheckedChange(!checked)}
      disabled={disabled}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Ayarlar</h1>
        <p className="text-muted-foreground">
          Sistem ayarlarınızı ve tercihlerinizi yönetin
        </p>
      </div>

      {/* Save Changes Bar */}
      {hasChanges && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <p className="text-orange-800 font-medium">Kaydedilmemiş değişiklikler var</p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={resetSettings}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Sıfırla
                </Button>
                <Button size="sm" onClick={saveSettings}>
                  <Save className="h-4 w-4 mr-1" />
                  Kaydet
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Bell className="h-5 w-5" />
              <span>Bildirim Ayarları</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium mb-4">Bildirim Kanalları</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">E-posta Bildirimleri</p>
                      <p className="text-sm text-muted-foreground">Önemli güncellemeler için e-posta al</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.email}
                    onCheckedChange={(value) => handleNotificationChange('email', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Tarayıcı Bildirimleri</p>
                      <p className="text-sm text-muted-foreground">Anlık bildirimler</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.push}
                    onCheckedChange={(value) => handleNotificationChange('push', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">SMS Bildirimleri</p>
                      <p className="text-sm text-muted-foreground">Acil durumlar için SMS</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.sms}
                    onCheckedChange={(value) => handleNotificationChange('sms', value)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="font-medium mb-4">Bildirim Türleri</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Yeni vakalar</span>
                  <Switch
                    checked={notifications.newCases}
                    onCheckedChange={(value) => handleNotificationChange('newCases', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Durum güncellemeleri</span>
                  <Switch
                    checked={notifications.statusUpdates}
                    onCheckedChange={(value) => handleNotificationChange('statusUpdates', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Ekip atamaları</span>
                  <Switch
                    checked={notifications.assignments}
                    onCheckedChange={(value) => handleNotificationChange('assignments', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Acil durumlar</span>
                  <Switch
                    checked={notifications.emergencies}
                    onCheckedChange={(value) => handleNotificationChange('emergencies', value)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Sistem uyarıları</span>
                  <Switch
                    checked={notifications.systemAlerts}
                    onCheckedChange={(value) => handleNotificationChange('systemAlerts', value)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium flex items-center space-x-2">
                  <Volume2 className="h-4 w-4" />
                  <span>Sesli Bildirimler</span>
                </h4>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={notifications.soundEnabled}
                    onCheckedChange={(value) => handleNotificationChange('soundEnabled', value)}
                  />
                </div>
              </div>

              {notifications.soundEnabled && (
                <div className="space-y-3 pl-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">Yeni vakalar</span>
                      <button
                        type="button"
                        onClick={() => playNotificationSound('mobile_case')}
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                        title="Önizle"
                      >
                        ▶
                      </button>
                    </div>
                    <Switch
                      checked={notifications.soundNewCases}
                      onCheckedChange={(value) => handleNotificationChange('soundNewCases', value)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">Ekip atamaları</span>
                      <button
                        type="button"
                        onClick={() => playNotificationSound('team_assignment')}
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                        title="Önizle"
                      >
                        ▶
                      </button>
                    </div>
                    <Switch
                      checked={notifications.soundAssignments}
                      onCheckedChange={(value) => handleNotificationChange('soundAssignments', value)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">Acil durumlar</span>
                      <button
                        type="button"
                        onClick={() => playNotificationSound('unassigned_case')}
                        className="text-xs text-red-500 hover:text-red-700 underline"
                        title="Önizle"
                      >
                        ▶
                      </button>
                    </div>
                    <Switch
                      checked={notifications.soundEmergencies}
                      onCheckedChange={(value) => handleNotificationChange('soundEmergencies', value)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">Sistem uyarıları</span>
                      <button
                        type="button"
                        onClick={() => playNotificationSound('system')}
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                        title="Önizle"
                      >
                        ▶
                      </button>
                    </div>
                    <Switch
                      checked={notifications.soundSystemAlerts}
                      onCheckedChange={(value) => handleNotificationChange('soundSystemAlerts', value)}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground pt-1">
                    ▶ simgesine tıklayarak sesi önizleyebilirsiniz
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Display Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <span>Görünüm Tercihleri</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label>Dil</Label>
                <Select value={preferences.language} onValueChange={(value) => handlePreferenceChange('language', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tr">Türkçe</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tema</Label>
                <Select value={preferences.theme} onValueChange={(value) => handlePreferenceChange('theme', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Açık</SelectItem>
                    <SelectItem value="dark">Koyu</SelectItem>
                    <SelectItem value="system">Sistem</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Saat Dilimi</Label>
                <Select value={preferences.timezone} onValueChange={(value) => handlePreferenceChange('timezone', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe/Istanbul">İstanbul (UTC+3)</SelectItem>
                    <SelectItem value="Europe/London">Londra (UTC+0)</SelectItem>
                    <SelectItem value="America/New_York">New York (UTC-5)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tarih Formatı</Label>
                <Select value={preferences.dateFormat} onValueChange={(value) => handlePreferenceChange('dateFormat', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dd/mm/yyyy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="mm/dd/yyyy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="yyyy-mm-dd">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Saat Formatı</Label>
                <Select value={preferences.timeFormat} onValueChange={(value) => handlePreferenceChange('timeFormat', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">24 Saat</SelectItem>
                    <SelectItem value="12h">12 Saat (AM/PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>Sistem Tercihleri</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Otomatik Yenileme</p>
                  <p className="text-sm text-muted-foreground">Sayfaları otomatik olarak yenile</p>
                </div>
                <Switch
                  checked={preferences.autoRefresh}
                  onCheckedChange={(value) => handlePreferenceChange('autoRefresh', value)}
                />
              </div>

              {preferences.autoRefresh && (
                <div>
                  <Label>Yenileme Aralığı (saniye)</Label>
                  <Select 
                    value={preferences.refreshInterval.toString()} 
                    onValueChange={(value) => handlePreferenceChange('refreshInterval', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 saniye</SelectItem>
                      <SelectItem value="30">30 saniye</SelectItem>
                      <SelectItem value="60">1 dakika</SelectItem>
                      <SelectItem value="300">5 dakika</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Sayfa Başına Öğe Sayısı</Label>
                <Select 
                  value={preferences.itemsPerPage.toString()} 
                  onValueChange={(value) => handlePreferenceChange('itemsPerPage', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Gizlilik ve Güvenlik</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Çevrimiçi Durumu Göster</p>
                  <p className="text-sm text-muted-foreground">Diğer kullanıcılar durumunuzu görebilir</p>
                </div>
                <Switch
                  checked={privacy.showOnlineStatus}
                  onCheckedChange={(value) => handlePrivacyChange('showOnlineStatus', value)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Veri Dışa Aktarmaya İzin Ver</p>
                  <p className="text-sm text-muted-foreground">Kişisel verilerinizi dışa aktırabilirsiniz</p>
                </div>
                <Switch
                  checked={privacy.allowDataExport}
                  onCheckedChange={(value) => handlePrivacyChange('allowDataExport', value)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Kullanım Verilerini Paylaş</p>
                  <p className="text-sm text-muted-foreground">Ürün geliştirme için anonim veriler</p>
                </div>
                <Switch
                  checked={privacy.shareUsageData}
                  onCheckedChange={(value) => handlePrivacyChange('shareUsageData', value)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Analitik Verileri</p>
                  <p className="text-sm text-muted-foreground">Performans ve hata takibi</p>
                </div>
                <Switch
                  checked={privacy.enableAnalytics}
                  onCheckedChange={(value) => handlePrivacyChange('enableAnalytics', value)}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportData}
                disabled={!privacy.allowDataExport}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Verilerimi Dışa Aktar
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  Son güvenlik güncellemesi: {new Date().toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Information */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Database className="h-5 w-5" />
              <span>Sistem Bilgileri</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div 
                className="text-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setShowChangelog(true)}
                title="Değişiklikleri görmek için tıklayın"
              >
                <p className="text-sm text-muted-foreground">Sürüm</p>
                <p className="font-semibold text-blue-600 hover:text-blue-700">{changelog.version}</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Son Güncelleme</p>
                <p className="font-semibold">{changelog.date}</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Kullanıcı Rolü</p>
                <Badge variant="outline">{user?.role || 'Bilinmiyor'}</Badge>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Kurum</p>
                <p className="font-semibold text-sm">{user?.institution_name || 'Bilinmiyor'}</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Bell className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Sistem Duyurusu</p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>v3.1.0</strong> yayınlandı (10.05.2026) — Kurumlar Vaka Mutabakatı, Kota Bazlı Dinamik Fiyatlandırma, Çatı Kurum Mimarisi (external_parent_institution) ve Organizasyon Faaliyet Raporu. Sürüm notlarını görmek için <button onClick={() => setShowChangelog(true)} className="underline font-medium cursor-pointer bg-transparent border-0 p-0 text-green-800">buraya tıklayın</button>.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Changelog Modal */}
      {showChangelog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Sparkles className="h-6 w-6 text-blue-600" />
                <div>
                  <h2 className="text-2xl font-bold">Sürüm Notları</h2>
                  <p className="text-sm text-muted-foreground">{changelog.version} — {changelog.date}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowChangelog(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {changelog.versions.map((ver, vi) => (
                <div key={ver.version} className={vi > 0 ? 'pt-4 border-t' : ''}>
                  <div className="flex items-center space-x-3 pb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${ver.badgeClass}`}>
                      {ver.version}
                    </span>
                    <span className="text-xs text-gray-500">
                      {ver.date}{ver.label ? ` — ${ver.label}` : ''}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {ver.changes.map((change, index) => (
                      <div key={index} className={`flex items-start space-x-4 ${vi > 0 ? 'opacity-70' : ''}`}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${vi === 0 ? 'bg-green-100' : vi === 1 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <CheckCircle className={`h-5 w-5 ${vi === 0 ? 'text-green-600' : vi === 1 ? 'text-blue-500' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold mb-1 ${vi === 0 ? 'text-gray-900' : 'text-gray-700'}`}>{change.title}</h3>
                          <p className={`text-sm leading-relaxed ${vi === 0 ? 'text-gray-600' : 'text-gray-500'}`}>{change.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <Button onClick={() => setShowChangelog(false)}>
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage; 